import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { useState, useEffect } from 'react'
import { supabase } from './config/supabase'

// Import pages
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import ContactList from './pages/ContactList'
import ContactForm from './pages/ContactForm'
import FamilyList from './pages/FamilyList'
import FamilyForm from './pages/FamilyForm'
import Reports from './pages/Reports'

const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb', // Modern blue
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7c3aed', // Modern purple
      light: '#a78bfa',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: {
      main: '#ef4444',
    },
    warning: {
      main: '#f59e0b',
    },
    success: {
      main: '#10b981',
    },
    info: {
      main: '#3b82f6',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#f8fafc',
        },
      },
    },
  },
})

function App() {
  console.log('App component rendered') // Debug log
  const [session, setSession] = useState(null)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Session data:', session) // Debug log
      setSession(session)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', session) // Debug log
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div style={{ display: 'flex', minHeight: '100vh' }}>
          <Routes>
            <Route
              path="/"
              element={
                !session ? (
                  <Login />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                session ? (
                  <Dashboard />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/contacts"
              element={
                session ? (
                  <ContactList />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/contacts/new"
              element={
                session ? (
                  <ContactForm />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/contacts/:id"
              element={
                session ? (
                  <ContactForm />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/families"
              element={
                session ? (
                  <FamilyList />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/families/new"
              element={
                session ? (
                  <FamilyForm />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/families/:id"
              element={
                session ? (
                  <FamilyForm />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/reports"
              element={
                session ? (
                  <Reports />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  )
}

export default App
